/**
 * OAuthButtons — Google + GitHub one-click sign-in via Cognito federated providers.
 *
 * Constructs the Cognito hosted UI /authorize URL with the appropriate
 * identity_provider param and redirects the browser. On callback Cognito
 * returns an auth code which the /callback route (AuthCallbackPage) exchanges
 * for tokens via the token endpoint.
 *
 * Card #2316.
 */
import { colors, radii } from "../styles/theme";
import { useConfig } from "../context/ConfigContext";

interface OAuthButtonsProps {
  /** "signin" shows "Continue with …", "signup" shows "Sign up with …" */
  mode: "signin" | "signup";
}

function buildOAuthUrl(
  domain: string,
  clientId: string,
  redirectUri: string,
  provider: "Google" | "GitHub",
): string {
  const params = new URLSearchParams({
    response_type:     "code",
    client_id:         clientId,
    redirect_uri:      redirectUri,
    identity_provider: provider,
    scope:             "openid email profile",
  });
  return `${domain}/oauth2/authorize?${params.toString()}`;
}

// brand-exception: Google logo uses official Google brand colours (#4285F4 #34A853 #FBBC05 #EA4335 — not 2hives UI tokens)
const GOOGLE_LOGO = (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" focusable="false">
    <g>
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/> {/* brand-exception: Google blue */}
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/> {/* brand-exception: Google green */}
      <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/> {/* brand-exception: Google yellow */}
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/> {/* brand-exception: Google red */}
    </g>
  </svg>
);

const GITHUB_LOGO = (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="currentColor">
    <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0 1 12 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"/>
  </svg>
);

export function OAuthButtons({ mode }: OAuthButtonsProps) {
  const { clientId, cognitoDomain, oauthRedirectUri } = useConfig();
  const label = mode === "signin" ? "Continue with" : "Sign up with";

  const redirectUri =
    oauthRedirectUri ?? `${window.location.origin}/callback`;

  function handleGoogle() {
    window.location.href = buildOAuthUrl(cognitoDomain, clientId, redirectUri, "Google");
  }

  function handleGitHub() {
    window.location.href = buildOAuthUrl(cognitoDomain, clientId, redirectUri, "GitHub");
  }

  const btnBase: React.CSSProperties = {
    display:        "flex",
    alignItems:     "center",
    justifyContent: "center",
    gap:            10,
    width:          "100%",
    padding:        "0.72rem 1rem",
    minHeight:      44,
    borderRadius:   radii.sm,
    fontFamily:     "inherit",
    fontSize:       "0.92rem",
    fontWeight:     600,
    cursor:         "pointer",
    transition:     "all 0.15s ease",
  };

  const googleStyle: React.CSSProperties = {
    ...btnBase,
    background: "#fff",           // brand-exception: Google OAuth button white bg (Google brand guideline)
    border:     "1px solid #d0d5dd", // brand-exception: Google button border (Google brand guideline)
    color:      "#1a1a1a",        // brand-exception: Google button dark text (Google brand guideline)
  };

  const githubStyle: React.CSSProperties = {
    ...btnBase,
    background: "#24292e",        // brand-exception: GitHub button dark bg (GitHub brand guideline)
    border:     "1px solid #444d56", // brand-exception: GitHub button border (GitHub brand guideline)
    color:      "#fff",           // brand-exception: GitHub button white text (GitHub brand guideline)
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
      <button
        type="button"
        onClick={handleGoogle}
        style={googleStyle}
        aria-label={`${label} Google`}
        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = "#f5f5f5"; }} // brand-exception: Google hover (Google brand)
        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = "#fff"; }} // brand-exception: Google idle (Google brand)
      >
        {GOOGLE_LOGO}
        <span>{label} Google</span>
      </button>

      <button
        type="button"
        onClick={handleGitHub}
        style={githubStyle}
        aria-label={`${label} GitHub`}
        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = "#2f363d"; }} // brand-exception: GitHub hover (GitHub brand)
        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = "#24292e"; }} // brand-exception: GitHub idle (GitHub brand)
      >
        {GITHUB_LOGO}
        <span>{label} GitHub</span>
      </button>
    </div>
  );
}

/** Horizontal divider with "or" label between OAuth buttons and email form */
export function OAuthDivider() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
      <div style={{ flex: 1, height: 1, background: colors.borderSubtle }} />
      <span style={{ color: colors.textMuted, fontSize: "0.78rem", whiteSpace: "nowrap" }}>
        or continue with email
      </span>
      <div style={{ flex: 1, height: 1, background: colors.borderSubtle }} />
    </div>
  );
}
